import { APP_NAME } from '@fbwl/shared';
import './styles/overlay.css';
import { readRuntimeOptions, resolveServerUrl, setOrientation } from './core/runtime.js';
import { createStage } from './core/app.js';
import { flagTextures } from './core/textures.js';
import { BroadcastConnection } from './net/connection.js';
import { AudioEngine } from './audio/engine.js';
import { SpeechEngine } from './audio/speech.js';
import { Hud } from './ui/hud.js';
import { SceneDirector } from './scenes/director.js';

/**
 * Overlay entry point.
 *
 * Boot order matters: renderer first (so there is something on screen), then the
 * socket (so state starts flowing), then the flag textures in the background.
 * Audio waits for a gesture unless we are in OBS, where autoplay is permitted.
 */
async function boot(): Promise<void> {
  const options = readRuntimeOptions();

  // Before anything else: every module resolves its geometry from the design
  // size, and a scene built at the wrong one cannot be corrected afterwards.
  setOrientation(options.orientation);
  document.body.classList.add(options.orientation);

  const canvas = document.getElementById('stage') as HTMLCanvasElement | null;
  const bootScreen = document.getElementById('boot');
  const bootStatus = document.getElementById('boot-status');
  const bootButton = document.getElementById('boot-button');

  if (!canvas) throw new Error('#stage canvas is missing');
  if (options.obsMode) document.body.classList.add('obs');

  const setStatus = (text: string): void => {
    if (bootStatus) bootStatus.textContent = text;
  };

  setStatus('starting renderer');
  const stage = await createStage(canvas, options);

  const hud = new Hud(options.hud);
  const audio = new AudioEngine(options.musicVolume, options.sfxVolume, options.muted);
  const speech = new SpeechEngine(options.speech, options.voiceVolume);

  speech.onSpeakingChange = (speaking, seconds) => {
    if (speaking) audio.duck(0.3, seconds + 0.4);
  };

  const director = new SceneDirector({
    world: stage.world,
    foreground: stage.foreground,
    camera: stage.camera,
    filters: stage.filters,
    particles: stage.particles,
    audio,
    speech,
    hud,
    galaxy: stage.galaxy,
  });

  setStatus('connecting to the arena');
  const connection = new BroadcastConnection(resolveServerUrl(options));

  connection.onStatus((status) => {
    if (status === 'live') setStatus('on air');
    else if (status === 'reconnecting') setStatus('reconnecting');
    else if (status === 'offline') setStatus('arena unreachable - retrying');
  });

  connection.onState((state) => director.applyState(state));
  connection.onTick((tick) => director.applyTick(tick));
  connection.onEvent((event) => director.handleEvent(event));

  // Render loop. Pixi's ticker owns the timing; everything else is a consumer.
  let elapsed = 0;
  let fpsAccumulator = 0;
  let fpsFrames = 0;

  stage.app.ticker.add((ticker) => {
    const dt = Math.min(0.1, ticker.deltaMS / 1000);
    elapsed += dt;

    stage.galaxy.update(dt);
    stage.camera.update(dt, elapsed);
    stage.filters.update(dt, stage.camera.speed);
    stage.particles.update(dt);
    director.update(dt);
    hud.update(dt);

    fpsAccumulator += dt;
    fpsFrames += 1;
    if (fpsAccumulator >= 0.5) {
      hud.setFps(fpsFrames / fpsAccumulator);
      fpsAccumulator = 0;
      fpsFrames = 0;
    }
  });

  hud.ready();

  // Flags stream in behind the show; cards swap from their generated stand-in
  // to the real artwork as each one decodes.
  void flagTextures.preload(undefined, (loaded, total) => {
    if (loaded === total) {
      const failures = flagTextures.failureCount;
      if (failures > 0) {
        console.warn(
          `[fbwl] ${failures}/${total} flag files were unavailable - generated cards are in use. Run "npm run assets:flags".`,
        );
      }
    }
  });

  const enter = async (): Promise<void> => {
    bootScreen?.classList.add('hidden');
    try {
      await audio.start();
    } catch (error) {
      console.warn('[fbwl] audio could not start', error);
    }
  };

  if (options.obsMode) {
    void enter();
  } else {
    bootButton?.addEventListener('click', () => void enter(), { once: true });
    // A stream deck or a hotkey press counts too.
    window.addEventListener('keydown', (event) => {
      if (event.key === 'Enter' || event.key === ' ') void enter();
    });
  }

  // Small operator conveniences, deliberately keyboard-only so nothing can be
  // clicked by accident while the overlay is on air.
  let muted = options.muted;
  window.addEventListener('keydown', (event) => {
    if (event.key === 'm') {
      muted = !muted;
      audio.setMuted(muted);
    }
    if (event.key === 'h') document.getElementById('hud')?.classList.toggle('ready');
  });

  console.info(`[fbwl] ${APP_NAME} overlay ready (quality: ${options.quality.tier})`);
}

boot().catch((error) => {
  console.error('[fbwl] overlay failed to start', error);
  const status = document.getElementById('boot-status');
  if (status) status.textContent = 'failed to start - see the console';
});
